import React, { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LLink } from '../../lib/lang';

export const Container = ({ children, className = '' }) => (
  <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

export const Card = ({ children, className = '', as: Tag = 'div', ...rest }) => (
  <Tag className={`bg-[#F5F1E8] border-2 border-black p-5 sm:p-6 ${className}`} {...rest}>{children}</Tag>
);

export const Heading = ({ children, className = '', level = 2 }) => {
  const Tag = `h${level}`;
  return <Tag className={`font-bold ${className}`} style={{ fontFamily: 'Playfair Display, serif' }}>{children}</Tag>;
};

export const Btn = ({ to, href, onClick, children, variant = 'primary', disabled, loading, className = '', type = 'button', ...rest }) => {
  const cls = `engraved-button ${variant === 'primary' ? 'engraved-button-red' : ''} inline-flex items-center justify-center gap-2 ${
    disabled || loading ? 'opacity-60 pointer-events-none' : ''
  } ${className}`;
  const inner = (<>{loading && <Loader2 size={16} className="animate-spin" />}{children}</>);
  if (to) return <LLink to={to} className={cls} {...rest}>{inner}</LLink>;
  if (href) return <a href={href} className={cls} {...rest}>{inner}</a>;
  return <button type={type} onClick={onClick} disabled={disabled || loading} className={cls} {...rest}>{inner}</button>;
};

export const Spinner = ({ label }) => {
  const { t } = useTranslation();
  const text = label === undefined ? t('pl.common.loading') : label;
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-gray-600" role="status">
      <Loader2 className="animate-spin" size={22} /> <span>{text}</span>
    </div>
  );
};

export const Skeleton = ({ className = '' }) => <div className={`animate-pulse bg-black/10 ${className}`} />;

export const CardSkeletons = ({ count = 3 }) => (
  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
    {Array.from({ length: count }).map((_, i) => (
      <Card key={i}><Skeleton className="h-36 mb-4" /><Skeleton className="h-5 w-2/3 mb-3" /><Skeleton className="h-4 w-full mb-2" /><Skeleton className="h-4 w-4/5" /></Card>
    ))}
  </div>
);

export const EmptyState = ({ icon: Icon, title, text, action }) => (
  <Card className="text-center py-10">
    {Icon && <Icon size={36} className="mx-auto mb-3 text-gray-500" />}
    <p className="text-xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{title}</p>
    {text && <p className="text-gray-600 max-w-xl mx-auto">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </Card>
);

export const ErrorState = ({ text, onRetry }) => {
  const { t } = useTranslation();
  return (
    <Card className="text-center py-8 border-[#C41E3A]">
      <p className="text-[#C41E3A] font-bold mb-2">{text || t('pl.common.loadError')}</p>
      {onRetry && <Btn variant="secondary" onClick={onRetry}>{t('pl.common.retry')}</Btn>}
    </Card>
  );
};

// Заглушка вместо скриншота: мы не подставляем случайные картинки.
export const ImagePlaceholder = ({ label, className = '' }) => {
  const { t } = useTranslation();
  const text = label || t('pl.product.screenshot');
  return (
    <div className={`flex items-center justify-center bg-[#E8E3D5] border border-dashed border-black/40 text-gray-500 text-sm text-center px-3 ${className}`} aria-label={text}>
      {text}
    </div>
  );
};

export const Modal = ({ open, onClose, title, children, footer }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50" onMouseDown={onClose}>
      <div role="dialog" aria-modal="true" className="bg-[#F5F1E8] border-2 border-black max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onMouseDown={(e) => e.stopPropagation()}>
        {title && <Heading level={3} className="text-2xl mb-4">{title}</Heading>}
        {children}
        {footer && <div className="mt-6 flex flex-wrap gap-3 justify-end">{footer}</div>}
      </div>
    </div>
  );
};

export const ConfirmModal = ({ open, title, text, confirmLabel, onConfirm, onClose, busy }) => {
  const { t } = useTranslation();
  return (
    <Modal open={open} onClose={onClose} title={title || t('pl.common.confirmTitle')}
      footer={(<><Btn variant="secondary" onClick={onClose}>{t('pl.common.cancel')}</Btn><Btn onClick={onConfirm} loading={busy}>{confirmLabel || t('pl.common.confirm')}</Btn></>)}>
      <p className="text-gray-700">{text}</p>
    </Modal>
  );
};

export const Field = ({ label, hint, children }) => (
  <label className="block mb-4">
    <span className="block text-sm font-bold mb-1">{label}</span>
    {children}
    {hint && <span className="block text-xs text-gray-500 mt-1">{hint}</span>}
  </label>
);

export const inputCls = 'w-full border-2 border-black bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]';

// Простое отображение текста уроков/новостей: абзацы по пустым строкам, без HTML (защита от XSS).
export const PlainText = ({ text, className = '' }) => (
  <div className={`space-y-4 leading-relaxed ${className}`}>
    {(text || '').split(/\n{2,}/).filter(Boolean).map((p, i) => (
      <p key={i} className="whitespace-pre-line">{p}</p>
    ))}
  </div>
);

export const StatusBadge = ({ children, tone = 'gray' }) => {
  const tones = {
    gray: 'bg-gray-200 text-gray-800', green: 'bg-green-100 text-green-800', red: 'bg-red-100 text-red-800',
    yellow: 'bg-yellow-100 text-yellow-800', blue: 'bg-blue-100 text-blue-800',
  };
  return <span className={`inline-block px-2 py-0.5 text-xs font-bold ${tones[tone]}`}>{children}</span>;
};
